import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges
} from '@angular/core';
import { Store } from '@ngrx/store';
import { combineLatest, concat, delay, of, ReplaySubject, Subscription } from 'rxjs';
import { distinctUntilChanged, filter, map, mergeMap, switchMap } from 'rxjs/operators';
import { ThemeService } from 'common/theme/theme.service';
import { GenesysChatWorkItem } from 'common/workitems/interfaces/genesys-chat-item.interface';
import { WorkAreaManager } from 'common/workitems/work-area-manager.service';
import { MessageBusEvents } from 'common/common/constants/message-bus-events';
import { RxEmitter } from 'common/rxjs/rx-emitter';
import {
  selectMessageConversationById,
  selectMessageConversationTypeById,
  selectMessageConversationStateIsOpen,
  selectWrapupPrompt
} from 'common/store/genesys-cloud/conversations/messages/selectors/message-conversation.selectors';
import { selectContactById } from 'common/store/genesys-cloud/external-contacts/selectors/external-contact.selectors';
import {
  selectIntegrationsByConversation,
  selectOpenIntegrationPanels
} from 'common/store/genesys-cloud/work-item-integrations/selectors/work-item-integrations.selectors';
import {
  setAllowedIntegrationPanels,
  toggleIntegrationPanelVisibility
} from 'common/store/genesys-cloud/work-item-integrations/actions/work-item-integrations.actions';
import { closeConversationWorkItem } from 'common/store/genesys-cloud/conversations/common/actions/conversation-work-item.actions';
import { UserPermission } from 'common/store/genesys-cloud/users/models/user-permission.enum';
import { selectUserHasPermission } from 'common/store/genesys-cloud/users/selectors/permission.selectors';
import { selectFeatureToggle } from 'common/store/genesys-cloud/feature-toggles/selectors/feature-toggles.selectors';
import { FeatureToggles } from 'common/store/genesys-cloud/feature-toggles/model/feature-toggles.model';
import { toggleCobrowseVisibility } from 'common/store/genesys-cloud/cobrowse/actions/genesys-cobrowse.actions';
import {
  selectDeploymentIdByConversationId,
  selectRemainingTimeToRespondMessageByConversationId
} from 'common/store/genesys-cloud/conversations/messages/selectors/message.selectors';
import { selectCobrowseActive } from 'common/store/genesys-cloud/cobrowse/selectors/genesys-cobrowse.selectors';
import { selectCobrowseEnabled } from 'common/store/genesys-cloud/conversations/web-deployments/selectors/configuration.selectors';
import { loadConfigurations } from 'common/store/genesys-cloud/conversations/web-deployments/actions/configuration.actions';
import { MessageType } from 'common/store/genesys-cloud/conversations/common/models/message-type.enum';

@Component({
  selector: 'app-genesys-chat-widget',
  templateUrl: './genesys-chat-widget.component.html',
  styleUrls: ['./genesys-chat-widget.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class GenesysChatWidgetComponent implements OnChanges, OnInit, OnDestroy {
  private readonly conversationIdSubject = new ReplaySubject<string>(1);
  readonly conversationId$ = this.conversationIdSubject.pipe(
    filter((conversationId) => !!conversationId),
    distinctUntilChanged()
  );
  readonly conversation$ = this.conversationId$.pipe(
    filter((conversationId) => !!conversationId),
    distinctUntilChanged(),
    switchMap((conversationId) => this.store.select(selectMessageConversationById(conversationId)))
  );
  readonly agentParticipantId$ = this.conversation$.pipe(map((conversation) => conversation.agentParticipant.id));
  readonly contact$ = this.conversation$.pipe(
    map((conversation) => conversation.customerParticipant.externalContactId),
    distinctUntilChanged(),
    switchMap((contactId) => this.store.select(selectContactById(contactId)))
  );
  readonly themeCssClassName$ = this.conversationId$.pipe(
    map((conversationId) => this.themeService.getThemeId(conversationId)),
    map((themeId) => this.themeService.getThemeCssClassName(themeId))
  );
  readonly avatarThemeId$ = this.conversationId$.pipe(
    map((conversationId) => this.themeService.getAvatarThemeId(conversationId))
  );
  readonly integrations$ = this.conversation$.pipe(
    switchMap((conversation) => this.store.select(selectIntegrationsByConversation(conversation)))
  );
  readonly openIntegrationPanels$ = this.conversationId$.pipe(
    switchMap((conversationId) => this.store.select(selectOpenIntegrationPanels(conversationId)))
  );

  readonly isCobrowseOpen$ = this.conversationId$.pipe(
    switchMap((conversationId) => this.store.select(selectCobrowseActive(conversationId)))
  );

  readonly isConversationOpen$ = this.conversationId$.pipe(
    switchMap((conversationId) => this.store.select(selectMessageConversationStateIsOpen(conversationId)))
  );
  readonly isWrapUpRequired$ = this.conversationId$.pipe(
    mergeMap((conversationId) => this.store.select(selectWrapupPrompt(conversationId)))
  );
  readonly conversationType$ = this.conversationId$.pipe(
    switchMap((conversationId) => this.store.select(selectMessageConversationTypeById(conversationId)))
  );
  readonly isConversationAcceptable$ = this.conversation$.pipe(map((conversation) => conversation.isAcceptable));

  readonly deploymentId$ = this.conversationId$.pipe(
    filter((conversationId) => !!conversationId),
    distinctUntilChanged(),
    switchMap((conversationId) => this.store.select(selectDeploymentIdByConversationId(conversationId)))
  );

  private readonly remainingTime$ = this.conversationId$.pipe(
    mergeMap((conversationId) => this.store.select(selectRemainingTimeToRespondMessageByConversationId(conversationId)))
  );

  private readonly CobrowsePermission$ = this.store.select(
    selectUserHasPermission(UserPermission.ConversationCobrowseAdd)
  );
  private readonly CobrowseFTApp$ = this.store.select(selectFeatureToggle(FeatureToggles.CobrowseInterapption));
  private readonly CobrowseFT$ = this.store.select(selectFeatureToggle(FeatureToggles.CobrowseV2));
  private readonly CobrowseEnabled$ = this.deploymentId$.pipe(
    distinctUntilChanged(),
    mergeMap((deploymentId) => this.store.select(selectCobrowseEnabled(deploymentId)))
  );

  readonly isCobrowseAllowed$ = combineLatest([
    this.CobrowseEnabled$,
    this.CobrowseFT$,
    this.CobrowseFTApp$,
    this.CobrowsePermission$
  ]).pipe(
    map(([enabled, featureToggle, featureToggleApp, permission]) => {
      this.isCobrowseAllowed = enabled && featureToggle && featureToggleApp && permission;
      return this.isCobrowseAllowed;
    })
  );

  readonly showCobrowseButton$ = combineLatest([this.isCobrowseAllowed$, this.conversationType$]).pipe(
    map(([allowed, conversationType]) => {
      this.showCobrowseButton = allowed && conversationType === MessageType.WebMessaging;
      return this.showCobrowseButton;
    })
  );

  // https://github.com/ReactiveX/rxjs/issues/3015
  private readonly timerMaxValue = 2147483647;

  readonly isMessagingPeriodEnded$ = this.remainingTime$.pipe(
    switchMap((remainingTime) => {
      if (remainingTime <= 0) {
        return of(true);
      } else if (remainingTime > 0 && remainingTime <= this.timerMaxValue) {
        return concat(of(false), of(true).pipe(delay(remainingTime)));
      } else {
        return of(false);
      }
    })
  );

  @Input()
  workItem?: GenesysChatWorkItem;

  private _conversationSubscription: Subscription;
  private _workItemToggleExpandedModeSubscription: Subscription;
  private _cobrowseToggleSubscription: Subscription;
  private _deploymentIdSubscription: Subscription;
  private _cobrowseOpenSubscription: Subscription;
  isCobrowseFeatureToggleEnabled: boolean;
  isCobrowseAppFeatureToggleEnabled: boolean;
  isCobrowsePermissionEnabled: boolean;
  isCobrowseAllowed = false;
  isCobrowseActive = false;
  showCobrowseButton = false;

  constructor(
    private readonly store: Store,
    private readonly themeService: ThemeService,
    private readonly workAreaManager: WorkAreaManager,
    private readonly rxEmitter: RxEmitter,
    private readonly changeDetector: ChangeDetectorRef
  ) {}

  ngOnChanges({ workItem }: SimpleChanges): void {
    if (workItem) {
      this.conversationIdSubject.next(workItem.currentValue?.id);
    }
  }

  ngOnInit(): void {
    this._conversationSubscription = this.conversation$.subscribe((conversation) => {
      this.store.dispatch(setAllowedIntegrationPanels({ conversation }));
    });

    this._workItemToggleExpandedModeSubscription = this.rxEmitter.subscribe(MessageBusEvents.ToggleExpandedMode, () => {
      this.changeDetector.detectChanges();
    });

    this._deploymentIdSubscription = this.deploymentId$.subscribe((deploymentId) => {
      this.store.dispatch(loadConfigurations({ deploymentId }));
    });

    this._cobrowseToggleSubscription = this.rxEmitter.subscribe(MessageBusEvents.ToggleCobrowse, (data) => {
      if (this.workAreaManager.getSelectedWorkItem().id === data.conversationId) {
        this.store.dispatch(
          toggleCobrowseVisibility({
            conversationId: data.conversationId,
            visibility: data.visibility
          })
        );
      }
    });

    this._cobrowseOpenSubscription = this.isCobrowseOpen$.subscribe((open) => {
      this.isCobrowseActive = open;
    });
  }

  ngOnDestroy(): void {
    this._conversationSubscription?.unsubscribe();
    this._workItemToggleExpandedModeSubscription?.unsubscribe();
    this._deploymentIdSubscription?.unsubscribe();
    this._cobrowseToggleSubscription?.unsubscribe();
    this._cobrowseOpenSubscription?.unsubscribe();
  }

  isExpandedMode(): boolean {
    return this.workAreaManager.isExpandedMode();
  }

  onMinimize(): void {
    this.store.dispatch(closeConversationWorkItem({ conversationId: this.workItem.id }));
  }

  onToggleExpanded(): void {
    this.rxEmitter.send(MessageBusEvents.ToggleExpandedMode, null);
    this.focusWorkItem();
  }

  onToggleCobrowse(currentWorkItemId: string): void {
    if (currentWorkItemId === this.workItem?.id) {
      this.rxEmitter.send(MessageBusEvents.ToggleCobrowse, {
        conversationId: currentWorkItemId,
        visibility: !this.isCobrowseActive
      });
    }
  }

  onToggleIntegration(integrationId: string): void {
    this.store.dispatch(toggleIntegrationPanelVisibility({ conversationId: this.workItem.id, integrationId }));
  }

  private focusWorkItem(): void {
    this.workAreaManager.focusWorkItem(this.workItem.id);
    this.store.dispatch(
      toggleCobrowseVisibility({
        conversationId: this.workAreaManager.getSelectedWorkItem().id,
        visibility: this.isCobrowseActive
      })
    );
  }
}
